import { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import PageTitle from "../../../layouts/PageTitle";
import { getUserById, updateUser } from "../../../../services/userMgmtApi";
import { authFetch } from "../../../../services/nodeAuthService";
import { API_BASE_URL } from "../../../../config/api";

const BASE_URL = `${API_BASE_URL}/api/users`;

/* ── helpers ─────────────────────────────────────────────────── */
function getStoredUser() {
  try {
    const p = JSON.parse(localStorage.getItem("userDetails") || "{}");
    return {
      userId: p?.userId || p?.localId || p?._id || "",
      userName: p?.userName || p?.name || "",
      imageUrl: p?.imageUrl || "",
    };
  } catch {
    return { userId: "", userName: "", imageUrl: "" };
  }
}

function resolveImage(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${API_BASE_URL}/${url.replace(/^\//, "")}`;
}

function getInitials(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

const inputStyle = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: "1px solid #e2e8f0", fontSize: 13, color: "#334155",
  background: "#fff", outline: "none", boxSizing: "border-box",
};
const readonlyStyle = {
  ...inputStyle,
  background: "#f8fafc", color: "#64748b", cursor: "default",
};
const labelStyle = {
  fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 4, display: "block",
};
const errStyle = { fontSize: 11, color: "#dc2626", marginTop: 3 };

/* ── component ───────────────────────────────────────────────── */
const UserProfile = () => {
  const { userId } = getStoredUser();

  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  /* edit-profile state */
  const [editMode,   setEditMode]   = useState(false);
  const [editName,   setEditName]   = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editImage,  setEditImage]  = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [saving,     setSaving]     = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const imageRef = useRef(null);

  /* change-password state */
  const [oldPwd,    setOldPwd]    = useState("");
  const [newPwd,    setNewPwd]    = useState("");
  const [confPwd,   setConfPwd]   = useState("");
  const [pwdErrors, setPwdErrors] = useState({});
  const [pwdSaving, setPwdSaving] = useState(false);

  /* ── fetch user ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    getUserById(userId)
      .then((data) => {
        const u = data?.results?.data || data?.result?.data || data?.data || data;
        setUser(u);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [userId]);

  /* ── enter edit mode ────────────────────────────────────────── */
  const startEdit = () => {
    setEditName(user?.userName || "");
    setEditMobile(user?.mobileNumber || "");
    setEditImage(null);
    setPreviewUrl(resolveImage(user?.imageUrl));
    setEditErrors({});
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditErrors({});
    if (imageRef.current) imageRef.current.value = "";
  };

  /* ── validate profile ───────────────────────────────────────── */
  const validateProfile = () => {
    const errs = {};
    if (!editName.trim()) errs.name = "Name is required";
    else if (editName.trim().length < 2) errs.name = "Name must be at least 2 characters";
    if (editMobile && !/^\d{10}$/.test(editMobile))
      errs.mobile = "Mobile number must be exactly 10 digits";
    return errs;
  };

  /* ── save profile ───────────────────────────────────────────── */
  const saveProfile = async () => {
    const errs = validateProfile();
    if (Object.keys(errs).length) { setEditErrors(errs); return; }

    setSaving(true);
    try {
      const payload = {
        userName:     editName.trim(),
        mobileNumber: editMobile.trim(),
        email:        user?.email,
        roleId:       user?.roleId?._id || user?.roleId,
        designation:  user?.designation || "",
      };
      if (editImage) payload.image = editImage;

      await updateUser(userId, payload);

      // Refresh user data
      const data = await getUserById(userId);
      const updated = data?.results?.data || data?.result?.data || data?.data || data;
      setUser(updated);

      // Sync localStorage
      try {
        const stored = JSON.parse(localStorage.getItem("userDetails") || "{}");
        stored.userName = updated.userName || stored.userName;
        stored.imageUrl = updated.imageUrl || stored.imageUrl;
        localStorage.setItem("userDetails", JSON.stringify(stored));
      } catch {}

      setEditMode(false);
      if (imageRef.current) imageRef.current.value = "";

      Swal.fire({ icon: "success", title: "Profile Updated", text: "Your profile has been updated successfully.", confirmButtonColor: "#6418c3" });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message || "Failed to update profile.", confirmButtonColor: "#6418c3" });
    } finally {
      setSaving(false);
    }
  };

  /* ── validate password ──────────────────────────────────────── */
  const validatePassword = () => {
    const errs = {};
    if (!oldPwd)               errs.old  = "Current password is required";
    if (!newPwd)               errs.new  = "New password is required";
    else if (newPwd.length < 6) errs.new = "New password must be at least 6 characters";
    if (!confPwd)              errs.conf = "Please confirm your new password";
    else if (newPwd !== confPwd) errs.conf = "Passwords do not match";
    return errs;
  };

  /* ── save password ──────────────────────────────────────────── */
  const savePassword = async () => {
    const errs = validatePassword();
    if (Object.keys(errs).length) { setPwdErrors(errs); return; }

    setPwdSaving(true);
    try {
      // Verify old password by attempting comparePassword via update with both
      // The API's updateUser runs user.save() which runs the bcrypt pre-save hook
      // We send oldPassword for server-side verification via a PATCH endpoint
      const res = await authFetch(`${BASE_URL}/${userId}/change-password`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ oldPassword: oldPwd, newPassword: newPwd }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.message || "Failed to change password");
      }

      setOldPwd(""); setNewPwd(""); setConfPwd(""); setPwdErrors({});
      Swal.fire({ icon: "success", title: "Password Changed", text: "Your password has been changed successfully.", confirmButtonColor: "#6418c3" });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message || "Failed to change password.", confirmButtonColor: "#6418c3" });
    } finally {
      setPwdSaving(false);
    }
  };

  /* ── render helpers ─────────────────────────────────────────── */
  const avatarSrc = resolveImage(user?.imageUrl);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2 text-muted" style={{ fontSize: 13 }}>Loading profile…</p>
      </div>
    );
  }

  return (
    <>
      <div className="row">

        {/* ── Profile Card ─────────────────────────────────── */}
        <div className="col-xl-6 col-lg-6">
          <div className="card" style={{ borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
            {/* Header */}
            <div className="card-header d-flex align-items-center justify-content-between" style={{ borderBottom: "1px solid #f1f5f9", padding: "16px 20px" }}>
              <h5 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1e293b" }}>
                <i className="fa fa-user-circle me-2 text-primary" />
                Personal Information
              </h5>
              {!editMode && (
                <button
                  className="btn btn-sm btn-outline-primary"
                  style={{ borderRadius: 8, fontWeight: 600, fontSize: 12 }}
                  onClick={startEdit}
                >
                  <i className="fa fa-pencil me-1" /> Edit
                </button>
              )}
            </div>

            <div className="card-body" style={{ padding: "24px 20px" }}>
              {/* Avatar */}
              <div className="d-flex align-items-center gap-3 mb-4">
                {(editMode ? previewUrl : avatarSrc) ? (
                  <img
                    src={editMode ? previewUrl : avatarSrc}
                    alt="profile"
                    style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", border: "3px solid #e2e8f0" }}
                  />
                ) : (
                  <div style={{
                    width: 72, height: 72, borderRadius: "50%",
                    background: "#6366f1", color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 22, fontWeight: 700,
                  }}>
                    {getInitials(user?.userName)}
                  </div>
                )}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: "#1e293b" }}>{user?.userName || "—"}</div>
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>{user?.designation || ""}</div>
                </div>
              </div>

              {/* Fields */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                {/* Name */}
                <div>
                  <label style={labelStyle}>Full Name</label>
                  {editMode ? (
                    <>
                      <input
                        style={{ ...inputStyle, borderColor: editErrors.name ? "#fca5a5" : "#e2e8f0" }}
                        value={editName}
                        onChange={(e) => { setEditName(e.target.value); setEditErrors((p) => ({ ...p, name: "" })); }}
                        placeholder="Enter full name"
                      />
                      {editErrors.name && <div style={errStyle}>{editErrors.name}</div>}
                    </>
                  ) : (
                    <div style={readonlyStyle}>{user?.userName || "—"}</div>
                  )}
                </div>

                {/* Email — always read-only */}
                <div>
                  <label style={labelStyle}>Email</label>
                  <div style={readonlyStyle}>{user?.email || "—"}</div>
                </div>

                {/* Mobile */}
                <div>
                  <label style={labelStyle}>Mobile Number</label>
                  {editMode ? (
                    <>
                      <input
                        style={{ ...inputStyle, borderColor: editErrors.mobile ? "#fca5a5" : "#e2e8f0" }}
                        value={editMobile}
                        onChange={(e) => { setEditMobile(e.target.value); setEditErrors((p) => ({ ...p, mobile: "" })); }}
                        placeholder="10-digit mobile number"
                        maxLength={10}
                      />
                      {editErrors.mobile && <div style={errStyle}>{editErrors.mobile}</div>}
                    </>
                  ) : (
                    <div style={readonlyStyle}>{user?.mobileNumber || "—"}</div>
                  )}
                </div>

                {/* Designation — read-only */}
                <div>
                  <label style={labelStyle}>Designation</label>
                  <div style={readonlyStyle}>{user?.designation || "—"}</div>
                </div>

                {/* Role — read-only */}
                <div>
                  <label style={labelStyle}>Role</label>
                  <div style={readonlyStyle}>{user?.roleId?.roleName || "—"}</div>
                </div>

                {/* Profile Picture upload — edit mode only */}
                {editMode && (
                  <div>
                    <label style={labelStyle}>Profile Picture <span style={{ color: "#94a3b8", fontWeight: 400 }}>(JPG / PNG, max 2 MB)</span></label>
                    <input
                      ref={imageRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      style={{ ...inputStyle }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        setEditImage(file);
                        const reader = new FileReader();
                        reader.onload = () => setPreviewUrl(reader.result);
                        reader.readAsDataURL(file);
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Edit-mode buttons */}
              {editMode && (
                <div className="d-flex gap-2 mt-4">
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ borderRadius: 8, fontWeight: 600, padding: "8px 20px" }}
                    onClick={saveProfile}
                    disabled={saving}
                  >
                    {saving ? <><i className="fa fa-spinner fa-spin me-1" />Saving…</> : <><i className="fa fa-check me-1" />Save Changes</>}
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    style={{ borderRadius: 8, fontWeight: 600, padding: "8px 20px" }}
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Change Password Card ─────────────────────────── */}
        <div className="col-xl-6 col-lg-6">
          <div className="card" style={{ borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
            <div className="card-header" style={{ borderBottom: "1px solid #f1f5f9", padding: "16px 20px" }}>
              <h5 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1e293b" }}>
                <i className="fa fa-lock me-2 text-warning" />
                Change Password
              </h5>
            </div>

            <div className="card-body" style={{ padding: "24px 20px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                {/* Old password */}
                <div>
                  <label style={labelStyle}>Current Password <span className="text-danger">*</span></label>
                  <input
                    type="password"
                    style={{ ...inputStyle, borderColor: pwdErrors.old ? "#fca5a5" : "#e2e8f0" }}
                    value={oldPwd}
                    onChange={(e) => { setOldPwd(e.target.value); setPwdErrors((p) => ({ ...p, old: "" })); }}
                    placeholder="Enter current password"
                  />
                  {pwdErrors.old && <div style={errStyle}>{pwdErrors.old}</div>}
                </div>

                {/* New password */}
                <div>
                  <label style={labelStyle}>New Password <span className="text-danger">*</span></label>
                  <input
                    type="password"
                    style={{ ...inputStyle, borderColor: pwdErrors.new ? "#fca5a5" : "#e2e8f0" }}
                    value={newPwd}
                    onChange={(e) => { setNewPwd(e.target.value); setPwdErrors((p) => ({ ...p, new: "", conf: "" })); }}
                    placeholder="Min. 6 characters"
                  />
                  {pwdErrors.new && <div style={errStyle}>{pwdErrors.new}</div>}
                </div>

                {/* Confirm password */}
                <div>
                  <label style={labelStyle}>Confirm New Password <span className="text-danger">*</span></label>
                  <input
                    type="password"
                    style={{ ...inputStyle, borderColor: pwdErrors.conf ? "#fca5a5" : "#e2e8f0" }}
                    value={confPwd}
                    onChange={(e) => { setConfPwd(e.target.value); setPwdErrors((p) => ({ ...p, conf: "" })); }}
                    placeholder="Re-enter new password"
                  />
                  {pwdErrors.conf && <div style={errStyle}>{pwdErrors.conf}</div>}
                </div>

              </div>

              <button
                className="btn btn-warning btn-sm mt-4"
                style={{ borderRadius: 8, fontWeight: 600, padding: "8px 20px", color: "#fff" }}
                onClick={savePassword}
                disabled={pwdSaving}
              >
                {pwdSaving ? <><i className="fa fa-spinner fa-spin me-1" />Changing…</> : <><i className="fa fa-key me-1" />Change Password</>}
              </button>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default UserProfile;